/**
 * Currency formatting and conversion utilities.
 *
 * Design decisions:
 *   - SEK is the source currency for all pricing logic
 *   - Conversion only happens at the presentation layer
 *   - Exchange rate is hardcoded (no live fetching in v1)
 *   - Uses Intl.NumberFormat for locale-correct output
 *
 * sv-SE  →  "12 500 kr"
 * intl   →  "€1,087"
 */

import type { Market } from "../domain/location/types";

/* ── Configuration ─────────────────────────────────────── */

/** Hardcoded SEK → EUR rate. Override via env in a future version. */
const SEK_TO_EUR = 1 / 11.5;

/* ── Formatters (cached for perf) ──────────────────────── */

const CURRENCY_LOCALES: Record<Market, string> = {
	se: "sv-SE",
	intl: "en-IE",
};

/**
 * One formatter per (market, precision). Constructing an `Intl.NumberFormat`
 * is the expensive part, so they are memoized rather than rebuilt per call.
 */
const FORMATTERS = new Map<string, Intl.NumberFormat>();

function priceFormatter(
	market: Market,
	maxFractionDigits: number,
): Intl.NumberFormat {
	const key = `${market}:${maxFractionDigits}`;
	let formatter = FORMATTERS.get(key);
	if (!formatter) {
		formatter = new Intl.NumberFormat(CURRENCY_LOCALES[market], {
			style: "currency",
			currency: getCurrencyCode(market),
			minimumFractionDigits: 0,
			maximumFractionDigits: maxFractionDigits,
			currencyDisplay: "symbol",
		});
		FORMATTERS.set(key, formatter);
	}
	return formatter;
}

/* ── Public API ────────────────────────────────────────── */

/**
 * Convert an amount in SEK to the target market currency, unrounded.
 *
 * Rounding is the formatter's job — there is exactly one conversion path in
 * this module so a configurable exchange rate has exactly one place to land.
 */
function convertUnrounded(amountSek: number, market: Market): number {
	return market === "se" ? amountSek : amountSek * SEK_TO_EUR;
}

/**
 * Convert an amount in SEK to the target market currency.
 * Returns the numeric value in the target currency, rounded to whole units.
 */
export function convertCurrency(amountSek: number, market: Market): number {
	return Math.round(convertUnrounded(amountSek, market));
}

export interface FormatPriceOptions {
	/**
	 * Decimals to keep. The default 0 is right for material budgets; a
	 * per-session running cost or a price per kWh needs 1, or whole-unit
	 * rounding collapses a 5 kr session to "€0" on intl.
	 */
	maxFractionDigits?: number;
}

/**
 * Format a price (given in SEK) for display in the target market.
 *
 * Examples:
 *   formatPrice(12500, 'se')                        → "12 500 kr"
 *   formatPrice(12500, 'intl')                      → "€1,087"
 *   formatPrice(5.1, 'intl', {maxFractionDigits:1}) → "€0.4"
 */
export function formatPrice(
	amountSek: number,
	market: Market,
	options?: FormatPriceOptions,
): string {
	const maxFractionDigits = options?.maxFractionDigits ?? 0;
	return priceFormatter(market, maxFractionDigits).format(
		convertUnrounded(amountSek, market),
	);
}

/**
 * Format a small price (session-scale amounts, given in SEK) for display.
 * Whole-unit rounding is fine for build budgets but turns a ~10 kr session
 * cost into "€0" on intl — thin wrapper over `formatPrice` kept for existing
 * callers; new call sites should just pass `{ maxFractionDigits: 1 }`.
 */
export function formatSmallPrice(amountSek: number, market: Market): string {
	return formatPrice(amountSek, market, { maxFractionDigits: 1 });
}

/**
 * Get the currency code string for a market.
 */
export function getCurrencyCode(market: Market): string {
	return market === "se" ? "SEK" : "EUR";
}
